import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { fetchWhatsAppSessions } from './whatsAppSessionThunks';
import { useWhatsAppSessionPolling } from './useWhatsAppSessionPolling';
import { WhatsAppInlineComposer } from './WhatsAppInlineComposer';
import { WhatsAppMessageBubbles } from './WhatsAppMessageBubbles';
import type { WhatsAppSession } from './whatsAppSessionTypes';

/**
 * WHATSAPP SESSION CHAT — corpo di una singola sessione: un piccolo header
 * (stato della finestra + numero messaggi) e la chat coi messaggi come bolle
 * (IN a sinistra, OUT a destra). Usato dentro l'accordion della timeline
 * generica: nella timeline c'è un accordion per sessione, e questo ne è il
 * contenuto espanso.
 *
 * Il composer inline compare solo se la finestra di 24h di Meta è APERTA, cioè se
 * il contatto ha scritto di recente. È una regola di Meta, non nostra: fuori dalla
 * finestra il testo libero verrebbe rifiutato, e prima ce ne accorgevamo solo dopo,
 * con un messaggio che nel CRM risultava inviato e al contatto non arrivava mai.
 * Quando la finestra è chiusa la scrittura passa dal popup, che ricade sui template.
 */
interface WhatsAppSessionChatProps {
  session: WhatsAppSession;
}

export function WhatsAppSessionChat({ session }: WhatsAppSessionChatProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Finché questo componente è montato l'accordion è aperto: i nuovi messaggi
  // arrivano da soli, senza ricaricare la pagina. Si spegne alla chiusura del
  // pannello e sulle sessioni scadute.
  useWhatsAppSessionPolling(session);

  const open = session.windowState === 'OPEN';

  return (
    <div className="whatsapp-session-chat">
      <div className="whatsapp-session__head">
        <span
          className={`whatsapp-session__badge is-${session.windowState.toLowerCase()}`}
        >
          {t(`whatsapp.window.state.${session.windowState}`)}
        </span>
        <span className="whatsapp-session__meta">
          {t('whatsapp.session.messages', { count: session.messageCount })}
        </span>
      </div>
      <WhatsAppMessageBubbles messages={session.messages} />
      {!open && (
        <p className="whatsapp-session__hint">
          {t(`whatsapp.window.hint.${session.windowState}`)}
        </p>
      )}
      {open && (
        <WhatsAppInlineComposer
          personId={session.personId}
          phoneNumber={session.phoneNumber}
          // Mittente fisso: il numero business della sessione, nessuna combo qui.
          phoneNumberId={session.phoneNumberId}
          // Refresh: il messaggio OUT si aggancia a questa sessione (id stabile,
          // l'accordion resta aperto) e ricompare nella chat.
          onSent={() => dispatch(fetchWhatsAppSessions({ personId: session.personId }))}
        />
      )}
    </div>
  );
}

export default WhatsAppSessionChat;
